import { FC, useEffect, useState } from 'react';
import useRouteParams from '../../../../../hooks/useRouteParams';
import { useDispatch, useSelector } from 'react-redux';
import {
  toggleControlBarShowing,
  updateControlBarComponents,
} from '../../../../../redux/ui/controlbar/actions';
import { useNavigate } from 'react-router-dom';
import Raptor2Loading from '../../../../feedback/Raptor2Loading';
import BackToOverviewBtn from '../../components/buttons/BackToOverviewBtn';

import TokenService from '../../../../../services/token-service';
import RaptorPDFViewer from '../../../../general/raptorPDFViewer/RaptorPDFViewer';
import { getOS } from '../../../../../utilities/helpers/common/system';
import { useGetKidData } from '../../../../../api/kids/hooks/queries';
import { pdfjs } from 'react-pdf';
import { RaidrV2RoutePaths } from '../../../../../routes/page-routes';
import ErrorBoundaryMessage from '../../../../ui/ErrorBoundaryMessage';
import { useShareClass } from '../../../../../api/shareclass/hooks/queries';
import { useFunds } from '../../../../../api/funds';
import {
  createPageDetailsSelector,
  createSectionByIdSelector,
} from '../../../../../redux/pages/selectors';
import { raptorPlatformIdSelector } from '../../../../../redux/raptorPlatform/selectors';
import { createResourceCacheKey } from '../../../../layout/general/SimpleGeneralPageWrapper';
import { clientNameSelector } from '../../../../../redux/auth/selectors';
import KidsEditorGridItem from '../layout/KidsEditorGridItem.component';
import { useGenerateKidDocument } from '../../../../../api/kids/hooks/mutations';
import { useRaidrKids } from '../../kids/context/RaidrKidsContext';
import { KidDocumentForm } from '../types/KidPdfGeneratorV2.types';

const KidsViewV2: FC = () => {
  const OS = getOS();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { backToOverviewLink, iframeTitle } = useRaidrKids();
  const { user_client_id: clientId } = TokenService.getUser()!;
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [hasError, setHasError] = useState(false);
  const clientName = useSelector(clientNameSelector);

  // Get route params
  const fundId = useRouteParams('fundId')['fundId'] || '';
  const shareClassId = useRouteParams('shareClassId')['shareClassId'] || '';
  const documentLanguage =
    useRouteParams('documentLanguage')['documentLanguage'] || 'English';
  const version = useRouteParams('version')['version'] || undefined;

  const { data, isPending, error } = useGetKidData(
    clientId,
    fundId,
    shareClassId,
    documentLanguage,
    version,
  );

  // Fetch fund and share class data for names
  const sectionDetails = useSelector(createSectionByIdSelector('raidr-v2'));
  const pageDetails = useSelector(
    createPageDetailsSelector('kid-view-v2', 'raidr-v2'),
  );
  const raptorPlatformId = useSelector(raptorPlatformIdSelector);
  const rsResources = createResourceCacheKey(
    sectionDetails?.rsResources,
    pageDetails?.rsResources,
  );

  const { data: fundsData } = useFunds({
    fundTypes: sectionDetails?.fundTypes,
    rsResources,
    raptorPlatformId,
  });

  const { data: shareClassData } = useShareClass(
    {
      client_id: clientId,
      fund_id: fundId,
      share_class_id: shareClassId || undefined,
    },
    {
      enabled: !!clientId && !!fundId && !!shareClassId,
    },
  );

  // Map IDs to names
  const fundName =
    fundsData?.find((f) => f.fund_id === fundId)?.fund_name_full ||
    fundsData?.find((f) => f.fund_id === fundId)?.fund_name ||
    fundId ||
    'Fund';
  const shareClassName =
    shareClassData?.[0]?.share_class_name_full ||
    shareClassData?.[0]?.share_class_name ||
    shareClassId ||
    'Share Class';
  const shareClassIsin =
    shareClassData?.[0]?.share_class_isin || shareClassId || '';

  const generateKidDocumentMutation = useGenerateKidDocument();

  useEffect(() => {
    return () => {
      dispatch(toggleControlBarShowing(false));
    };
  }, [dispatch]);

  useEffect(() => {
    if (!isPending && data) {
      refreshPdf();
    }
  }, [data, isPending]);

  useEffect(() => {
    if (data) {
      updateControlBar();
    }
  }, [data, fundName, shareClassIsin, version]);

  if (isPending) {
    return <Raptor2Loading centerWrap messages={[`Loading KID document...`]} />;
  }

  if (error) {
    return <ErrorBoundaryMessage />;
  }

  const refreshPdf = async () => {
    try {
      setHasError(false);
      if (!data) return;

      const payload: KidDocumentForm = {
        client: clientName || '',
        fund_name: fundId,
        fund_name_full: fundName,
        share_class: shareClassName,
        date: data[0].kid_id_create_date,
        elements: data as any,
      };

      const response = await generateKidDocumentMutation.mutateAsync(payload);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const fileUrl = window.URL.createObjectURL(blob);

      const iframe = document.querySelector('iframe');
      if (iframe?.src) {
        iframe.src = fileUrl;
        iframe.title = iframeTitle;
      }

      if (pdfUrl) URL.revokeObjectURL(pdfUrl);

      // Setting the worker for pdfjs:
      if (OS != 'linux') {
        pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;
      }

      setPdfUrl(fileUrl);
    } catch (error) {
      console.error('Failed to refresh PDF:', error);
      setHasError(true);
    }
  };

  const updateControlBar = () => {
    if (!data) return;

    const componentsList = [
      <BackToOverviewBtn
        backToOverview={() =>
          navigate(RaidrV2RoutePaths.KID_OVERVIEW_V2, {
            state: { fundId, shareClassId },
          })
        }
        dontSetPosition
        key="back_to_overview"
      />,
      <div className="ml-[0.62rem] mr-[0.62rem] flex max-h-[1.88rem] w-full min-w-0 items-center truncate whitespace-nowrap text-gray-800">
        <span className="truncate text-xl font-medium">{`${fundName} - ${shareClassName}`}</span>
      </div>,
    ];

    if (version) {
      componentsList.push(
        <div
          key="version"
          className="ml-[0.62rem] mr-[0.62rem] inline-flex h-[1.88rem] max-h-[1.88rem] items-center whitespace-nowrap rounded-[3px] bg-[#274C77] px-2 py-0.5 text-[0.75rem] font-semibold leading-none text-white shadow-sm"
        >
          <span className="text-lg font-medium leading-none">{`Version: ${version}`}</span>
        </div>,
      );
    }

    dispatch(updateControlBarComponents(componentsList));
    dispatch(toggleControlBarShowing(true));
  };

  return (
    <KidsEditorGridItem xs={12}>
      <div className="mx-auto w-full max-w-6xl">
        <RaptorPDFViewer
          pdfUrl={pdfUrl}
          fileName={`${fundName} - ${shareClassIsin} - ${version || 'current'}`}
          hasError={hasError}
          errorMessage={''}
          setHasError={setHasError}
          isPending={generateKidDocumentMutation.isPending}
          fitOnLoad
        />
      </div>
    </KidsEditorGridItem>
  );
};

export default KidsViewV2;
